import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, OnDestroy, OnInit, Signal, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { interval, merge, Subscription, timer } from 'rxjs';
import { concatMap, delay, delayWhen, map, repeat, switchMap, tap } from 'rxjs/operators';
import { JobService, UserDatasetRefreshLogDto, UserDatasetRefreshStageType, UserDatasetRefreshStatus, UserDatasetService } from 'src/app/services/api.services';
import { catchAppError } from 'src/app/shared/utils';
import { getRepeatDelay } from '../../../../pbir/visuals/data/impl/DataQuery/utils';

export interface RefreshUserDatasetProgressDialogData {
  userDatasetId: number
  latestRefreshLog: Signal<UserDatasetRefreshLogDto>;
  allowCancel: boolean;
}

@Component({
  selector: 'app-refresh-userdataset-progress-dialog',
  templateUrl: './refresh-userdataset-progress-dialog.component.html',
  styleUrls: ['./refresh-userdataset-progress-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class RefreshUserDatasetProgressDialogComponent implements OnInit {
  readonly destroyRef = inject(DestroyRef);
  readonly userDatasetService = inject(UserDatasetService);
  readonly jobService = inject(JobService);
  readonly dialog = inject(MatDialog);
  readonly dialogRef = inject(MatDialogRef<RefreshUserDatasetProgressDialogComponent>);
  readonly data = inject<RefreshUserDatasetProgressDialogData>(MAT_DIALOG_DATA);

  readonly currentStage = signal(UserDatasetRefreshModalState.Connecting);
  readonly targetStage = signal<UserDatasetRefreshModalState>(UserDatasetRefreshModalState.Connecting);

  userDatasetLog: UserDatasetRefreshLogDto;

  ngOnInit(): void {
    interval(250).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => {
        let [currentStage, targetStage] = [this.currentStage(), this.targetStage()];
        if (currentStage < targetStage)
          this.currentStage.update(x => x + 1);
      })
    ).subscribe();
  }

  private readonly _onLogRecivedEffect = effect(() => {
    let log = this.data.latestRefreshLog();
    if(log != null)
      untracked(()=>this.onLogRecived(log));
  })

  onLogRecived(log: UserDatasetRefreshLogDto) {
    this.userDatasetLog = log;
    this.mapLogToUiStages();
  }

  mapLogToUiStages() {
    // while the log is pending remain at the default ui state (connecting)
    if (this.userDatasetLog.status != UserDatasetRefreshStatus.Running)
      return;

    // Since a Refresh can have multiple datasets we get the dataset operations and display the state of the lowest one

    const datasetOperations = this.userDatasetLog.stages.filter(s => s.type == UserDatasetRefreshStageType.DatasetDataLoad);

    //of there are no dataset operations yet we remain at default
    if (datasetOperations.length < 1)
      return;

    const operationsStates: number[] = [];

    //get the current state of each dataset Operation
    for (const operation of datasetOperations) {
      let state = 0;

      if (operation.substages != null) {
        const connectionStage = operation.substages[UserDatasetRefreshModalState.Connecting];
        const executionStage = operation.substages[UserDatasetRefreshModalState.Execution]
        const transferStage = operation.substages[UserDatasetRefreshModalState.DataTransfer]

        if (connectionStage.status >= UserDatasetRefreshStatus.Running)
          state = UserDatasetRefreshModalState.Connecting;

        if (executionStage.status >= UserDatasetRefreshStatus.Running)
          state = UserDatasetRefreshModalState.Execution;

        if (transferStage.status >= UserDatasetRefreshStatus.Running)
          state = UserDatasetRefreshModalState.DataTransfer;

        if (transferStage.status >= UserDatasetRefreshStatus.Succeeded)
          state = UserDatasetRefreshModalState.Finalization;
      }

      operationsStates.push(state);
    }

    //display the lowest state
    this.targetStage.set(Math.min(...operationsStates));
  }
  
  cancellationRequested = signal(false);

  cancelRefresh() {
    if (this.cancellationRequested())
      return;

    this.cancellationRequested.set(true);

    this.userDatasetService.getRefreshJobId(this.data.userDatasetId).pipe(
      switchMap(jobId => this.jobService.cancel(jobId.id)),
      catchAppError()
    ).subscribe();
  }
}

export enum UserDatasetRefreshModalState{
  Connecting = 0,
  Execution = 1,
  DataTransfer = 2,
  Finalization = 3
}
